// codec.mjs
//
// Zero-dependency, framework-agnostic URL <-> selection codec for the
// catalog filter rewrite (Stage 0.6). No Vue, no vue-router, no network, no
// catalog data required at import time. Everything here operates on plain
// objects so it can run identically in the browser, in SSR, and under plain
// `node` in tests.
//
// -----------------------------------------------------------------------
// THE CONTRACT AT A GLANCE
// -----------------------------------------------------------------------
//
// Selection (the in-memory, logical shape; see `emptySelection()`):
//   {
//     facets: { [stableKey]: (number|string)[] },   // checkbox-kind groups only
//     ranges: { [stableKey]: { min?: number, max?: number } }, // range-kind groups (not price)
//     price:  { min?: number, max?: number },
//     search: string | undefined,
//     sort: string | undefined,
//     order: "asc" | "desc" | undefined,
//     page: number,                                  // always >= 1, default 1
//   }
//
// queryObject (the URL-adjacent shape; matches Vue Router's LocationQuery /
// this repo's own `PaginationQuery` convention in utils/pagination.ts):
//   Record<string, string | string[] | undefined>
//
// KEY NAMING SCHEME
//   - Every checkbox-kind facet is a bare stable key, e.g. `frame_shape`,
//     holding numeric legacy ids or stable `[a-z0-9_-]+` option keys.
//   - Every range-kind facet (including the always-present "price" pseudo
//     facet) is a `<key>_min` / `<key>_max` pair, e.g. `lens_width_min`,
//     `price_max`. This is one uniform rule for every bounded numeric
//     parameter instead of price having its own special-cased names.
//   - Structural, non-facet keys keep the same names already used
//     elsewhere in this codebase's query handling (see category.vue's
//     GraphQL variables and utils/pagination.ts): `page`, `sort`, `order`,
//     `search`.
//   - Stable facet keys come from ONE table: `./groupMap.mjs`. Nothing in
//     this file hardcodes a filter_group_id.
//
// ORDERING RULE (determinism)
//   Every key that ends up in an encoded queryObject is inserted in plain
//   ascending lexicographic order (`Array.prototype.sort()` on the key
//   strings, the JS default). This is independent of the order fields were
//   set on the input Selection object, and independent of how the facet
//   table happens to be declared. Because none of our keys look like array
//   indices, `Object.keys()` on a freshly built object equals insertion
//   order, so inserting in sorted order makes key enumeration order
//   deterministic. Multi-value facet arrays are normalized, deduplicated and
//   sorted (numeric ids numerically, stable keys lexicographically).
//
// REPEATED PARAMS, NOT COMMA/BRACKET SYNTAX
//   Multi-value facets are represented as an *array* value on the
//   queryObject (`{ frame_shape: ["12", "45"] }`), matching Vue Router's own
//   LocationQuery convention and this repo's existing
//   `utils/pagination.ts` (`PaginationQueryValue` already includes
//   `Array<...>`, and `buildPaginationHref` already iterates array values
//   with `params.append` per element -- i.e. the existing router/tooling
//   already treats "array value on a plain query object" as "repeated
//   standard query parameter" with no bracket or comma syntax involved).
//   This module never needs to invent bracket (`key[]=`) or comma
//   (`key=a,b`) syntax: it hands the router a plain object with array
//   values, and the router's own serializer (or `toQueryString` below, for
//   SSR/tests) turns that into repeated `key=a&key=b`. `decode` additionally
//   tolerates a comma-joined single string for the same key defensively
//   (hand-edited/shared links), even though `encode` never produces one.
//
// -----------------------------------------------------------------------

import { FACET_GROUPS, facetByGroupId } from "./groupMap.mjs";

export const STRUCTURAL_KEYS = Object.freeze([
  "page",
  "sort",
  "order",
  "search",
  "price_min",
  "price_max",
]);

const ORDER_VALUES = new Set(["asc", "desc"]);
const STABLE_FACET_VALUE = /^[a-z0-9][a-z0-9_-]*$/;

// ---------------------------------------------------------------------------
// Selection helpers
// ---------------------------------------------------------------------------

export function emptySelection() {
  return {
    facets: {},
    ranges: {},
    price: {},
    search: undefined,
    sort: undefined,
    order: undefined,
    page: 1,
  };
}

function cloneSelection(selection) {
  return {
    facets: Object.fromEntries(
      Object.entries(selection.facets || {}).map(([k, v]) => [k, [...v]])
    ),
    ranges: Object.fromEntries(
      Object.entries(selection.ranges || {}).map(([k, v]) => [k, { ...v }])
    ),
    price: { ...(selection.price || {}) },
    search: selection.search,
    sort: selection.sort,
    order: selection.order,
    page: selection.page ?? 1,
  };
}

// ---------------------------------------------------------------------------
// Small parsing primitives (used by both decode and migration)
// ---------------------------------------------------------------------------

function isFiniteNumberString(raw) {
  if (raw === null || raw === undefined) return false;
  const trimmed = String(raw).trim();
  if (trimmed === "") return false;
  return Number.isFinite(Number(trimmed));
}

function toNumber(raw) {
  return Number(String(raw).trim());
}

function asArray(value) {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : [value];
}

// Flattens repeated-param arrays AND defensively splits comma-joined
// strings, so `?k=1&k=2`, `?k=1,2` and a mix of both all normalize the same
// way on decode. encode() never produces the comma form itself.
function flattenCommaTolerant(rawValue) {
  const out = [];
  for (const item of asArray(rawValue)) {
    if (item === undefined || item === null) continue;
    for (const piece of String(item).split(",")) {
      const trimmed = piece.trim();
      if (trimmed !== "") out.push(trimmed);
    }
  }
  return out;
}

function normalizeFacetValue(raw) {
  const value = String(raw ?? "").trim();
  if (/^\d+$/.test(value)) return Number(value);
  if (STABLE_FACET_VALUE.test(value)) return value;
  return undefined;
}

function compareFacetValues(left, right) {
  if (typeof left === "number" && typeof right === "number") return left - right;
  if (typeof left === "number") return -1;
  if (typeof right === "number") return 1;
  return String(left).localeCompare(String(right));
}

function normalizeFacetValues(values) {
  const normalized = values
    .map(normalizeFacetValue)
    .filter((value) => value !== undefined);
  return [...new Set(normalized)].sort(compareFacetValues);
}

function makeError(type, fields, message) {
  return { type, ...fields, message };
}

// ---------------------------------------------------------------------------
// Legacy migration
// ---------------------------------------------------------------------------
//
// Reads the CURRENT (pre-Stage-0.6) production query format and rewrites it,
// key by key, into the new stable-key format so existing shared and
// search-engine-indexed links keep resolving to the same catalog view.
//
// Recognised legacy keys:
//   filter=... / filters=...        comma-joined oc_filter.filter_id values
//                                    (both spellings accepted; the real
//                                    frontend code -- catalog-filters.vue,
//                                    category.vue's GraphQL variables --
//                                    uses `filters` on the route and `filter`
//                                    as the GraphQL variable name; this repo
//                                    never actually emits a bare `filter=`
//                                    query param, but it is accepted
//                                    defensively)
//   min_price / max_price           -> price_min / price_max
//   min_filters_<groupId> /
//   max_filters_<groupId>           -> <stableKey>_min / <stableKey>_max
//                                       (groupId resolved via groupMap.mjs)
//
// KNOWN LIMITATION -- read before relying on this for `filter(s)`:
// `oc_filter.filter_id` (the option id) does not, by itself, say which
// filter_group_id it belongs to; the legacy `filters=12,45,77` query string
// never carried that information (only `min_filters_<groupId>` /
// `max_filters_<groupId>` embed a group id, because *those* keys are named
// after the group). To translate a legacy option id into `{ stableKey:
// [id] }` this function needs an option_id -> group_id index, which is
// *catalog* data (Stage 0.4 "Facet source-of-truth матриця", not yet built
// anywhere in this repo) -- it is NOT something a zero-dependency, offline
// URL codec can know on its own. Callers that have that data available at
// runtime (the live `api/filters` response already nests options under
// their group) should pass it in as `optionGroupIndex`. Without it, legacy
// `filter(s)=` ids cannot be losslessly migrated to grouped stable keys and
// are dropped -- loudly, as a `legacy_unmigratable` issue (see decode()),
// never silently.
//
// @param {Record<string, unknown>} query
// @param {{ optionGroupIndex?: Map<number, number> }} [opts]
export function migrateLegacyQuery(query, opts = {}) {
  const { optionGroupIndex } = opts;
  const out = {};
  const warnings = []; // [{ key, detail }]
  const migratedKeys = [];
  const consumed = new Set();

  const q = query || {};

  // min_price / max_price -> price_min / price_max
  if (q.min_price !== undefined && q.price_min === undefined) {
    out.price_min = q.min_price;
    migratedKeys.push("min_price");
  } else if (q.min_price !== undefined) {
    warnings.push({
      key: "min_price",
      detail: "both legacy min_price and new-style price_min present; price_min wins, min_price ignored",
    });
  }
  consumed.add("min_price");

  if (q.max_price !== undefined && q.price_max === undefined) {
    out.price_max = q.max_price;
    migratedKeys.push("max_price");
  } else if (q.max_price !== undefined) {
    warnings.push({
      key: "max_price",
      detail: "both legacy max_price and new-style price_max present; price_max wins, max_price ignored",
    });
  }
  consumed.add("max_price");

  // min_filters_<id> / max_filters_<id> -> <stableKey>_min / <stableKey>_max
  for (const rawKey of Object.keys(q)) {
    const minMatch = /^min_filters_(\d+)$/.exec(rawKey);
    const maxMatch = /^max_filters_(\d+)$/.exec(rawKey);
    const match = minMatch || maxMatch;
    if (!match) continue;

    consumed.add(rawKey);
    const groupId = Number(match[1]);
    const entry = facetByGroupId(groupId);

    if (!entry || entry.kind !== "range") {
      warnings.push({
        key: rawKey,
        detail: `filter_group_id ${groupId} is not a known range facet in groupMap.mjs; dropped`,
      });
      continue;
    }

    const targetKey = minMatch ? `${entry.key}_min` : `${entry.key}_max`;
    out[targetKey] = q[rawKey];
    migratedKeys.push(rawKey);
  }

  // filter= / filters= (comma-joined legacy option ids)
  const legacyFilterRaw = [
    ...flattenCommaTolerant(q.filter),
    ...flattenCommaTolerant(q.filters),
  ];
  if (q.filter !== undefined) consumed.add("filter");
  if (q.filters !== undefined) consumed.add("filters");

  if (legacyFilterRaw.length > 0) {
    if (!optionGroupIndex) {
      warnings.push({
        key: "filter(s)",
        detail: `legacy filter(s) param present with ${legacyFilterRaw.length} id(s) but no optionGroupIndex supplied -- cannot determine which facet group they belong to; all dropped: [${legacyFilterRaw.join(", ")}]`,
      });
    } else {
      const dropped = [];
      for (const rawId of legacyFilterRaw) {
        const optionId = Number(rawId);
        const groupId = Number.isFinite(optionId) ? optionGroupIndex.get(optionId) : undefined;
        const entry = groupId !== undefined ? facetByGroupId(groupId) : undefined;
        if (!entry || entry.kind !== "checkbox") {
          dropped.push(rawId);
          continue;
        }
        const existing = asArray(out[entry.key]);
        out[entry.key] = [...existing, String(optionId)];
      }
      if (dropped.length > 0) {
        warnings.push({
          key: "filter(s)",
          detail: `${dropped.length} legacy option id(s) had no known checkbox group in optionGroupIndex/groupMap; dropped: [${dropped.join(", ")}]`,
        });
      }
    }
    migratedKeys.push(legacyFilterRaw.length > 0 ? "filter(s)" : undefined);
  }

  // Everything else (already new-style keys, or genuinely unknown keys)
  // passes through for decode() to classify. If a key was ALSO just
  // produced by legacy translation above (e.g. a URL that mixes
  // `filters=12` with a new-style `frame_shape=45`), merge rather than
  // silently letting one side clobber the other -- both describe "selected
  // option ids for this facet," so union is the only choice that doesn't
  // drop data.
  for (const [key, value] of Object.entries(q)) {
    if (consumed.has(key)) continue;
    if (out[key] === undefined) {
      out[key] = value;
    } else {
      out[key] = [...asArray(out[key]), ...asArray(value)];
    }
  }

  return { query: out, warnings, migratedKeys: migratedKeys.filter(Boolean) };
}

// ---------------------------------------------------------------------------
// decode
// ---------------------------------------------------------------------------
//
// Decision record for each invalid-input class (see report for the full
// reasoning); summary:
//   - unknown_key            -> strict decode fails closed; best-effort drops
//                                the key and keeps going.
//   - malformed_number       -> strict fails closed; best-effort drops just
//                                that value (never guesses/coerces).
//   - range_reversed         -> strict fails closed; best-effort drops BOTH
//                                bounds of the offending range rather than
//                                silently swapping min/max (swapping would
//                                make a malformed link look like a valid,
//                                different one).
//   - invalid_page           -> strict fails closed; best-effort clamps to
//                                page 1, which is always a valid landing
//                                page regardless of result-set size (the one
//                                case where a fallback isn't a guess).
//   - legacy_unmigratable    -> strict fails closed; best-effort drops the
//                                un-migratable legacy data (see
//                                migrateLegacyQuery's doc comment).
//   - unexpected_multi_value -> a scalar key (page/sort/order/search/*_min/
//                                *_max) received more than one value; strict
//                                fails closed, best-effort keeps the first
//                                value deterministically (arrays from a
//                                spec-compliant router are already ordered).
//   - invalid_enum           -> `order` outside {asc, desc}; strict fails
//                                closed, best-effort drops it (no sort order
//                                is safer than guessing one).
//
// Catalog-level validity (does option id 45 actually exist in group 2 right
// now?) is deliberately OUT OF SCOPE here, mirroring
// dev/reference-evaluator/evaluator.mjs's own SelectionValidationError split:
// this codec validates URL *shape*; the query engine (or the evaluator, for
// testing) is the authority on whether an id that is shaped correctly still
// refers to something that exists.

function parseCore(query, groupMap) {
  const errors = [];
  const selection = emptySelection();
  const seenKeys = new Set();
  const groupByKey = new Map(groupMap.map((entry) => [entry.key, entry]));

  const recognizedRangeSuffixKeys = new Set();
  for (const entry of groupMap) {
    if (entry.kind === "range") {
      recognizedRangeSuffixKeys.add(`${entry.key}_min`);
      recognizedRangeSuffixKeys.add(`${entry.key}_max`);
    }
  }

  for (const rawKey of Object.keys(query || {})) {
    if (query[rawKey] === undefined) continue;
    seenKeys.add(rawKey);

    const isStructural = STRUCTURAL_KEYS.includes(rawKey);
    const facetEntry = groupByKey.get(rawKey);
    const isCheckboxFacet = facetEntry && facetEntry.kind === "checkbox";
    const isRangeSuffix = recognizedRangeSuffixKeys.has(rawKey);

    if (!isStructural && !isCheckboxFacet && !isRangeSuffix) {
      errors.push(
        makeError("unknown_key", { key: rawKey }, `Unknown query key "${rawKey}"`)
      );
      continue;
    }

    if (rawKey === "page") {
      const values = asArray(query[rawKey]);
      if (values.length > 1) {
        errors.push(
          makeError(
            "unexpected_multi_value",
            { key: "page", raw: values },
            `"page" was given ${values.length} values; expected exactly one`
          )
        );
        selection.page = 1;
        continue;
      }
      const raw = values[0];
      if (!/^\d+$/.test(String(raw).trim())) {
        errors.push(
          makeError("invalid_page", { raw }, `"page" value "${raw}" is not a positive integer`)
        );
        selection.page = 1;
        continue;
      }
      const page = Number(raw);
      if (!Number.isSafeInteger(page) || page < 1) {
        errors.push(
          makeError("invalid_page", { raw }, `"page" value "${raw}" is out of range`)
        );
        selection.page = 1;
        continue;
      }
      selection.page = page;
      continue;
    }

    if (rawKey === "sort" || rawKey === "search") {
      const values = asArray(query[rawKey]);
      if (values.length > 1) {
        errors.push(
          makeError(
            "unexpected_multi_value",
            { key: rawKey, raw: values },
            `"${rawKey}" was given ${values.length} values; expected exactly one`
          )
        );
        selection[rawKey] = String(values[0]).trim() || undefined;
        continue;
      }
      const trimmed = String(values[0] ?? "").trim();
      selection[rawKey] = trimmed === "" ? undefined : trimmed;
      continue;
    }

    if (rawKey === "order") {
      const values = asArray(query[rawKey]);
      if (values.length > 1) {
        errors.push(
          makeError(
            "unexpected_multi_value",
            { key: "order", raw: values },
            `"order" was given ${values.length} values; expected exactly one`
          )
        );
        continue;
      }
      const normalized = String(values[0] ?? "").trim().toLowerCase();
      if (normalized === "") {
        selection.order = undefined;
        continue;
      }
      if (!ORDER_VALUES.has(normalized)) {
        errors.push(
          makeError(
            "invalid_enum",
            { key: "order", raw: values[0], allowed: [...ORDER_VALUES] },
            `"order" value "${values[0]}" is not one of asc|desc`
          )
        );
        continue;
      }
      selection.order = normalized;
      continue;
    }

    if (rawKey === "price_min" || rawKey === "price_max") {
      const values = asArray(query[rawKey]);
      if (values.length > 1) {
        errors.push(
          makeError(
            "unexpected_multi_value",
            { key: rawKey, raw: values },
            `"${rawKey}" was given ${values.length} values; expected exactly one`
          )
        );
        continue;
      }
      const raw = values[0];
      if (!isFiniteNumberString(raw)) {
        errors.push(
          makeError("malformed_number", { key: rawKey, raw }, `"${rawKey}" value "${raw}" is not a number`)
        );
        continue;
      }
      selection.price[rawKey === "price_min" ? "min" : "max"] = toNumber(raw);
      continue;
    }

    if (isCheckboxFacet) {
      const values = flattenCommaTolerant(query[rawKey]);
      const ids = [];
      let hadInvalid = false;
      for (const raw of values) {
        const normalized = normalizeFacetValue(raw);
        if (normalized === undefined) {
          errors.push(
            makeError(
              "malformed_facet_value",
              { key: rawKey, raw },
              `Facet "${rawKey}" contains an unsafe value "${raw}"`
            )
          );
          hadInvalid = true;
          continue;
        }
        ids.push(normalized);
      }
      if (ids.length > 0) {
        const unique = [...new Set(ids)].sort(compareFacetValues);
        selection.facets[rawKey] = unique;
      } else if (!hadInvalid) {
        // empty value (e.g. `?frame_shape=`) -- not an error, just "no selection"
      }
      continue;
    }

    if (isRangeSuffix) {
      const entry = groupMap.find(
        (e) => e.kind === "range" && (rawKey === `${e.key}_min` || rawKey === `${e.key}_max`)
      );
      const isMin = rawKey.endsWith("_min");
      const values = asArray(query[rawKey]);
      if (values.length > 1) {
        errors.push(
          makeError(
            "unexpected_multi_value",
            { key: rawKey, raw: values },
            `"${rawKey}" was given ${values.length} values; expected exactly one`
          )
        );
        continue;
      }
      const raw = values[0];
      if (!isFiniteNumberString(raw)) {
        errors.push(
          makeError("malformed_number", { key: rawKey, raw }, `"${rawKey}" value "${raw}" is not a number`)
        );
        continue;
      }
      if (!selection.ranges[entry.key]) selection.ranges[entry.key] = {};
      selection.ranges[entry.key][isMin ? "min" : "max"] = toNumber(raw);
      continue;
    }
  }

  // Reversed-range checks (price + every numeric facet range)
  if (
    selection.price.min !== undefined &&
    selection.price.max !== undefined &&
    selection.price.min > selection.price.max
  ) {
    errors.push(
      makeError(
        "range_reversed",
        { key: "price", min: selection.price.min, max: selection.price.max },
        `price_min (${selection.price.min}) > price_max (${selection.price.max})`
      )
    );
    delete selection.price.min;
    delete selection.price.max;
  }

  for (const [key, range] of Object.entries(selection.ranges)) {
    if (range.min !== undefined && range.max !== undefined && range.min > range.max) {
      errors.push(
        makeError(
          "range_reversed",
          { key, min: range.min, max: range.max },
          `${key}_min (${range.min}) > ${key}_max (${range.max})`
        )
      );
      delete selection.ranges[key];
    }
  }

  return { selection, errors };
}

function decodeInternal(query, opts) {
  const groupMap = opts?.groupMap || FACET_GROUPS;
  const { query: migrated, warnings } = migrateLegacyQuery(query, opts);
  const { selection, errors } = parseCore(migrated, groupMap);

  for (const w of warnings) {
    errors.push(
      makeError("legacy_unmigratable", { key: w.key }, w.detail)
    );
  }

  return { selection, errors };
}

/**
 * Strict decode: fails closed on ANY issue (unknown key, malformed number,
 * reversed range, out-of-bounds page, un-migratable legacy data). Intended
 * for contexts that must never fabricate a plausible-but-wrong canonical
 * URL: SSR canonicalization, sitemap generation, redirect resolution.
 *
 * @returns {{ ok: true, selection: object, errors: [] } | { ok: false, selection: null, errors: object[] }}
 */
export function decode(query, opts = {}) {
  const { selection, errors } = decodeInternal(query, opts);
  if (errors.length > 0) return { ok: false, selection: null, errors };
  return { ok: true, selection, errors: [] };
}

/**
 * Lenient decode: never fails closed. Invalid/unmigratable pieces are
 * dropped (never guessed) and always reported in `errors`, so callers can
 * still render a working page while surfacing "some filters in this link
 * could not be applied." Intended for the actual category/search page.
 *
 * @returns {{ selection: object, errors: object[] }}
 */
export function decodeBestEffort(query, opts = {}) {
  return decodeInternal(query, opts);
}

/**
 * Convenience wrapper over the strict decoder that throws instead of
 * returning a discriminated result. Useful for SSR route guards / redirect
 * middleware that already work in throw/catch style.
 */
export function decodeOrThrow(query, opts = {}) {
  const result = decode(query, opts);
  if (!result.ok) {
    const err = new Error(
      `filter-url decode failed: ${result.errors.map((e) => e.message).join("; ")}`
    );
    err.name = "FilterUrlDecodeError";
    err.issues = result.errors;
    throw err;
  }
  return result.selection;
}

// ---------------------------------------------------------------------------
// encode
// ---------------------------------------------------------------------------
//
// `encode` assumes it is handed an already-valid Selection (the output of
// decode/decodeBestEffort/applyChange -- i.e. a programmer-controlled value,
// not raw user input). A facet/range key that isn't in groupMap.mjs is a
// contract violation by the caller, not user input, so encode throws rather
// than silently dropping it -- the opposite failure mode from decode, and
// deliberately so: decode's job is "never trust the URL"; encode's job is
// "never silently emit an inconsistent URL from a value this module itself
// produced."

export function encode(selection, opts = {}) {
  const groupMap = opts.groupMap || FACET_GROUPS;
  const byKey = new Map(groupMap.map((e) => [e.key, e]));
  const entries = [];

  for (const [key, ids] of Object.entries(selection.facets || {})) {
    if (!ids || ids.length === 0) continue;
    const entry = byKey.get(key);
    if (!entry || entry.kind !== "checkbox") {
      throw new Error(
        `encode(): "${key}" in selection.facets is not a known checkbox facet in groupMap`
      );
    }
    const unique = normalizeFacetValues(ids);
    if (unique.length !== ids.length && ids.some((id) => normalizeFacetValue(id) === undefined)) {
      throw new Error(`encode(): "${key}" contains an unsafe facet value`);
    }
    entries.push([key, unique.map(String)]);
  }

  for (const [key, range] of Object.entries(selection.ranges || {})) {
    const entry = byKey.get(key);
    if (!entry || entry.kind !== "range") {
      throw new Error(
        `encode(): "${key}" in selection.ranges is not a known range facet in groupMap`
      );
    }
    if (range?.min !== undefined) entries.push([`${key}_min`, String(range.min)]);
    if (range?.max !== undefined) entries.push([`${key}_max`, String(range.max)]);
  }

  if (selection.price?.min !== undefined) entries.push(["price_min", String(selection.price.min)]);
  if (selection.price?.max !== undefined) entries.push(["price_max", String(selection.price.max)]);

  if (selection.search) entries.push(["search", String(selection.search)]);
  if (selection.sort) entries.push(["sort", String(selection.sort)]);
  if (selection.order) entries.push(["order", String(selection.order)]);

  const page = selection.page ?? 1;
  if (page > 1) entries.push(["page", String(page)]);

  entries.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return Object.fromEntries(entries);
}

/**
 * Deterministic string serialization of an already-sorted queryObject
 * (as produced by `encode`), for tests and any SSR path that needs a literal
 * query string rather than a router-consumed object. Array values become
 * repeated params, in array order (encode() already sorts them).
 */
export function toQueryString(queryObject) {
  const params = [];
  for (const [key, value] of Object.entries(queryObject)) {
    if (Array.isArray(value)) {
      for (const v of value) params.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
    } else if (value !== undefined && value !== null) {
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return params.join("&");
}

// ---------------------------------------------------------------------------
// Page reset -- the "impossible to forget" API
// ---------------------------------------------------------------------------
//
// encode()/decode() are pure (de)serializers; they never merge "current
// query" with "requested change." The ONLY supported way to go from "current
// URL state" to "next URL state" is nextQuery(), built on applyChange().
// applyChange()'s switch has NO default-preserve case: every change kind
// must appear explicitly and decide, right there, whether it resets `page`.
// Adding a new semantic change kind without adding a case throws instead of
// silently preserving `page` -- that is what makes forgetting the reset
// structurally impossible rather than merely "documented."

export const CHANGE_KINDS = Object.freeze({
  SET_FACET: "setFacet",
  TOGGLE_FACET_VALUE: "toggleFacetValue",
  CLEAR_FACET: "clearFacet",
  SET_RANGE: "setRange",
  CLEAR_RANGE: "clearRange",
  SET_PRICE: "setPrice",
  CLEAR_PRICE: "clearPrice",
  SET_SORT: "setSort",
  SET_SEARCH: "setSearch",
  RESET_ALL: "resetAll",
  PAGINATE: "paginate",
});

const PAGE_RESETTING_KINDS = Object.freeze(
  Object.values(CHANGE_KINDS).filter((k) => k !== CHANGE_KINDS.PAGINATE)
);
export { PAGE_RESETTING_KINDS };

function requireFacetKey(groupMap, key, kind) {
  const entry = groupMap.find((e) => e.key === key);
  if (!entry || entry.kind !== kind) {
    throw new Error(`applyChange(): "${key}" is not a known ${kind} facet in groupMap`);
  }
  return entry;
}

export function applyChange(selection, change, opts = {}) {
  const groupMap = opts.groupMap || FACET_GROUPS;
  const next = cloneSelection(selection);

  switch (change.kind) {
    case CHANGE_KINDS.SET_FACET: {
      requireFacetKey(groupMap, change.key, "checkbox");
      const unique = normalizeFacetValues(change.values || []);
      if (
        unique.length !== (change.values || []).length &&
        (change.values || []).some((value) => normalizeFacetValue(value) === undefined)
      ) {
        throw new Error(`applyChange(): "${change.key}" contains an unsafe facet value`);
      }
      if (unique.length > 0) next.facets[change.key] = unique;
      else delete next.facets[change.key];
      next.page = 1;
      return next;
    }
    case CHANGE_KINDS.TOGGLE_FACET_VALUE: {
      requireFacetKey(groupMap, change.key, "checkbox");
      const current = new Set(next.facets[change.key] || []);
      const value = normalizeFacetValue(change.value);
      if (value === undefined) {
        throw new Error(`applyChange(): "${change.key}" contains an unsafe facet value`);
      }
      if (current.has(value)) current.delete(value);
      else current.add(value);
      const unique = [...current].sort(compareFacetValues);
      if (unique.length > 0) next.facets[change.key] = unique;
      else delete next.facets[change.key];
      next.page = 1;
      return next;
    }
    case CHANGE_KINDS.CLEAR_FACET: {
      requireFacetKey(groupMap, change.key, "checkbox");
      delete next.facets[change.key];
      next.page = 1;
      return next;
    }
    case CHANGE_KINDS.SET_RANGE: {
      requireFacetKey(groupMap, change.key, "range");
      const range = {};
      if (change.min !== undefined) range.min = Number(change.min);
      if (change.max !== undefined) range.max = Number(change.max);
      if (Object.keys(range).length > 0) next.ranges[change.key] = range;
      else delete next.ranges[change.key];
      next.page = 1;
      return next;
    }
    case CHANGE_KINDS.CLEAR_RANGE: {
      requireFacetKey(groupMap, change.key, "range");
      delete next.ranges[change.key];
      next.page = 1;
      return next;
    }
    case CHANGE_KINDS.SET_PRICE: {
      const price = {};
      if (change.min !== undefined) price.min = Number(change.min);
      if (change.max !== undefined) price.max = Number(change.max);
      next.price = price;
      next.page = 1;
      return next;
    }
    case CHANGE_KINDS.CLEAR_PRICE: {
      next.price = {};
      next.page = 1;
      return next;
    }
    case CHANGE_KINDS.SET_SORT: {
      if (change.sort !== undefined) next.sort = change.sort || undefined;
      if (change.order !== undefined) next.order = change.order || undefined;
      next.page = 1;
      return next;
    }
    case CHANGE_KINDS.SET_SEARCH: {
      next.search = change.search || undefined;
      next.page = 1;
      return next;
    }
    case CHANGE_KINDS.RESET_ALL: {
      return emptySelection();
    }
    case CHANGE_KINDS.PAGINATE: {
      const page = Math.max(1, Math.floor(Number(change.page) || 1));
      next.page = page;
      return next;
    }
    default:
      // Exhaustiveness guard: a new change kind MUST be added above with an
      // explicit page-reset decision. There is deliberately no fallthrough
      // that would silently preserve `page`.
      throw new Error(
        `applyChange(): unhandled change kind "${change?.kind}". Every change kind must ` +
          `explicitly decide whether it resets pagination -- add a case above, do not rely on a default.`
      );
  }
}

/**
 * The single recommended entrypoint for turning "current URL query" +
 * "a UI change" into "next URL query," with page-reset handled by
 * construction. This is the function catalog-filters.vue / category.vue
 * should call instead of hand-building `{ ...route.query, ... }` patches.
 */
export function nextQuery(currentQuery, change, opts = {}) {
  const { selection } = decodeBestEffort(currentQuery, opts);
  const next = applyChange(selection, change, opts);
  return encode(next, opts);
}

// ---------------------------------------------------------------------------
// Evaluator-shape adapter
// ---------------------------------------------------------------------------
//
// Converts a URL-level Selection into the plain-object shape
// dev/reference-evaluator/evaluator.mjs's evaluate() consumes:
//   { filters: { [groupId]: number[] }, ranges: { [groupId]: {min,max} },
//     price: {min,max}, context }
// `context` (customerGroupId, now, storeId, categoryId, ...) is not URL
// state (it comes from session/route-path/server clock), so the caller must
// supply it. This function does not import evaluator.mjs -- it only builds
// a structurally compatible plain object -- keeping this module
// dependency-free.

export function toEvaluatorSelection(selection, context, opts = {}) {
  const groupMap = opts.groupMap || FACET_GROUPS;
  const byKey = new Map(groupMap.map((e) => [e.key, e]));
  const filters = {};
  const ranges = {};

  for (const [key, ids] of Object.entries(selection.facets || {})) {
    const entry = byKey.get(key);
    if (!entry) continue;
    filters[entry.groupId] = [...ids];
  }
  for (const [key, range] of Object.entries(selection.ranges || {})) {
    const entry = byKey.get(key);
    if (!entry) continue;
    ranges[entry.groupId] = { ...range };
  }

  return {
    filters,
    ranges,
    price: { ...(selection.price || {}) },
    context,
  };
}
